// Copied from apps/admin/src/features/map-view/components/StepPickupLayer.tsx (PRD #875).
import React, { useMemo, useState } from "react";
import { useMapEvents } from "react-leaflet";
import type { ConnectProto } from "@racoon/api";
import { PickupGeofenceLayer, type PickupGeofenceLayerProps } from "./PickupGeofences";
import { useZoomLevel } from "../model/pickup-geofence";
import { jobStatusTone } from "../model/constants/job-status-colors";
import { STEP_FAN_MIN_ZOOM, collapseStepPins, type StepPinItem } from "../model/helpers/step-pins";

const colourKey = (status?: ConnectProto.JobStatus) =>
  status == null ? undefined : jobStatusTone(status)?.bg;

// Any step pin, a lone one included: a route whose sites each serve one step
// still needs the zoom bands, or its pins keep their numbers when zoomed out.
const isStepPin = (item: StepPinItem) => item.fan != null;

/**
 * PickupGeofenceLayer for maps whose pins are route steps: fans when zoomed in,
 * one pin per site further out (a click fans that site). Never clustered: one
 * route's sites are few, and a cluster left its GPS stops passing for its jobs.
 */
export function StepPickupLayer<T extends StepPinItem>(props: PickupGeofenceLayerProps<T>) {
  const zoom = useZoomLevel();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  useMapEvents({ click: () => setExpandedKey(null) });

  const hasSteps = useMemo(() => props.items.some(isStepPin), [props.items]);
  const fanAll = zoom >= STEP_FAN_MIN_ZOOM;
  const items = useMemo(
    () =>
      !hasSteps || fanAll
        ? props.items
        : collapseStepPins(props.items, expandedKey, setExpandedKey, colourKey),
    [props.items, hasSteps, fanAll, expandedKey],
  );

  if (!hasSteps) return <PickupGeofenceLayer {...props} />;
  return <PickupGeofenceLayer {...props} items={items} cluster={false} />;
}
